import { Injectable } from "@angular/core";
import { doc, docData, DocumentReference, Firestore, setDoc } from "@angular/fire/firestore";
import { Store } from "@ngrx/store";
import { firstValueFrom, Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";
import { ShopHomeService } from "src/app/shop/pages/shop-home/shop-home.service";
import { FireSubService } from "../../firebase/fireSub.service";
import { AppState } from "../app.state";
import { resetSettings, setBackground, setBetPresets, setBetSizes, setCardStyle, setFelt, setPosition, setRaiseSizes, setThemeOptions, setTransactions, setUnlockedAvatars, setUnlockedBackgrounds, setUnlockedCardStyles, setUnlockedFelts } from "./gameThemeSettings.actions";
import { Background, BetPresets, BetSizes, CardStyle, Felt, GameThemeSettings, GameThemeSettingsState, INITIAL_BET_PRESETS, INITIAL_THEME_OPTIONS, IAvatar, RaiseSizes, ThemeOptions, Transaction } from "./gameThemeSettings.state";


function parseFraction(fraction: string): number {
    const parts = fraction.split('/');
    if (parts.length === 2) {
        const numerator = parseFloat(parts[0]);
        const denominator = parseFloat(parts[1]);
        if (denominator !== 0) return Math.round((numerator / denominator) * 100);
    }
    const num = parseFloat(fraction);
    if (!isNaN(num)) return Math.round(num * 100);
    return 50;
}

function migrateBetSizesToPresets(betSizes: BetSizes): BetPresets {
    return {
        noLimit: {
            preFlop: { ...INITIAL_BET_PRESETS.noLimit.preFlop },
            postFlop: {
                preset1: parseFraction(betSizes.bet1),
                preset2: parseFraction(betSizes.bet2),
                preset3: parseFraction(betSizes.bet3),
            },
        },
        plo: { ...INITIAL_BET_PRESETS.plo },
    };
}

@Injectable({
    providedIn: 'root'
})
export class GameThemeSettingsDispatcher {
    settingsSubscription: Subscription;

    constructor(
        private store: Store<AppState>,
        private shopService: ShopHomeService,
        private firestore: Firestore,
        private fireSubService: FireSubService
    ) {
    }

    getPrivateSettings(userID: string) {
        return doc(this.firestore, `users/${userID}/private/settings`) as DocumentReference<GameThemeSettings>;
    }

    subscibeToThemeSettings(userID: string) {
        if (this.settingsSubscription) this.settingsSubscription.unsubscribe();
        this.settingsSubscription = docData<GameThemeSettings>(this.getPrivateSettings(userID))
        .pipe(
            filter((settings) => settings !== undefined)
        ).subscribe(
            settings => {
                if (settings === undefined) return;
                if (settings.felt) {
                    if (!settings.felt.logoColor) {
                        if (settings.felt.type === 'externalUrl') settings.felt.logoColor = '#FFFFFF';
                        else settings.felt.logoColor = '#000000';
                    }
                    settings.felt.id = this.convertId(settings.felt.id);
                    this.setFelt(settings.felt);
                }
                if (settings.background) {
                    settings.background.id = this.convertId(settings.background.id);
                    this.setBackground(settings.background);
                }
                if (settings.cardStyle) this.setCardStyle(settings.cardStyle);
                if (settings.raiseSizes) this.setRaiseSizes(settings.raiseSizes);
                if (settings.betSizes) this.setBetSizes(settings.betSizes);
                if (settings.betPresets) {
                    this.setBetPresets(settings.betPresets);
                } else if (settings.betSizes) {
                    const migrated = migrateBetSizesToPresets(settings.betSizes);
                    this.setBetPresets(migrated);
                    setDoc(this.getPrivateSettings(userID), { betPresets: migrated }, { merge: true })
                        .catch(error => console.error('Failed to migrate betPresets:', error));
                } else {
                    this.setBetPresets(INITIAL_BET_PRESETS);
                }
                this.setThemeOptionsFromFirestore(settings, userID);
            }
        );
        this.fireSubService.registerSubscription(this.settingsSubscription);
    }

    setThemeOptionsFromFirestore(settings: GameThemeSettings, userID: string) {
        if(settings.showAvatars === undefined) {
            this.saveOldValueOfShowAvatars(userID);
        }
        const themeOptionsKeysArray = Object.keys(INITIAL_THEME_OPTIONS);
        Object.keys(settings).forEach((key) => {
            if(themeOptionsKeysArray.find(k => k === key)) {
                this.setThemeOptions(key as keyof ThemeOptions, settings[key]);
            }
        })
    }

    saveOldValueOfShowAvatars(userID: string) {
        firstValueFrom(this.store
            .select((state: AppState) => state.profile)
            .pipe(
                filter(profileState => profileState.profileData !== null),
                map((profileState) => {
                    if(profileState.profileData) return profileState.profileData.defaultAvatar
                }),
                map(showAvatar => showAvatar === undefined ? true : showAvatar),
            ))
            .then((showAvatars) => {
                setDoc(this.getPrivateSettings(userID), {showAvatars}, {merge: true});
            });
    }

    getGameThemeSettingsState(): Observable<GameThemeSettingsState> {
        return this.store.select('gameThemeSetting');
    }

    resetSettings() {
        this.store.dispatch(resetSettings());
    }

    setFelt(felt: Felt) {
        this.store.dispatch(setFelt({felt}));
    }

    setBackground(background: Background) {
        this.store.dispatch(setBackground({background}));
    }

    setCardStyle(cardStyle: CardStyle) {
        this.store.dispatch(setCardStyle({cardStyle}));
    }

    setBetSizes(betSizes: BetSizes) {
        this.store.dispatch(setBetSizes({betSizes}));
    }

    setBetPresets(betPresets: BetPresets) {
        this.store.dispatch(setBetPresets({betPresets}));
    }

    getBetPresets(): Observable<BetPresets> {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.betPresets),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        );
    }

    setRaiseSizes(raiseSizes: RaiseSizes) {
        this.store.dispatch(setRaiseSizes({raiseSizes}));
    }

    setThemeOptions(optionType: keyof ThemeOptions, optionValue: boolean) {
        this.store.dispatch(setThemeOptions({optionType, optionValue}));
    }

    setTransactions(transactions: Transaction[]) {
        this.store.dispatch(setTransactions({transactions}));
    }

    setPosition(position: number) {
        this.store.dispatch(setPosition({position}));
    }


    async getPurchasedFelts() {
        try {
            const unlockedFelts = await this.shopService.getUnlockedItems<Felt>('felts');
            this.store.dispatch(setUnlockedFelts({felts: unlockedFelts}));
        } catch (error) {
            console.error(error);
        }
    }
    
    async getPurchasedBackgrounds() {
        try {
            const unlockedBackgrounds = await this.shopService.getUnlockedItems<Background>('backgrounds');
            this.store.dispatch(setUnlockedBackgrounds({backgrounds: unlockedBackgrounds}));
        } catch (error) {
            console.error(error);
        }
    }

    async getPurchasedCardStyles() {
        try {
            const unlockedCardStyles = await this.shopService.getUnlockedItems<CardStyle>('cardStyles');
            this.store.dispatch(setUnlockedCardStyles({cardStyles: unlockedCardStyles}));
        } catch (error) {
            console.error(error);
        }
    }

    public async getPurchasedAvatars() {
        try {
            const unlockedAvatars = await this.shopService.getUnlockedItems<IAvatar>('avatars');
            this.store.dispatch(setUnlockedAvatars({avatars: unlockedAvatars.map(avatar => ({id: avatar.id, url: avatar.url}))}));
        } catch (error) {
            console.error(error);
        }
    }

    getMyFeltsState() {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.unlockedFelts),
            distinctUntilChanged((prev, current) => prev.length === current.length),
        );
    }

    getMyBackgroundsState() {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.unlockedBackgrounds),
            distinctUntilChanged((prev, current) => prev.length === current.length),
        );
    }

    getMyCardStylesState() {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.unlockedCardStyles),
            distinctUntilChanged((prev, current) => prev.length === current.length),
        );
    }

    getMyAvatarsState(): Observable<IAvatar[]> {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.unlockedAvatars),
        );
    }

    getActiveFelt() {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.felt),
        );
    }

    getActiveBackground() {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.background),
        );
    }

    getActiveCardStyle() {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings.cardStyle),
        );
    }

    getThemeOptions(optionType: keyof ThemeOptions) {
        return this.store.select('gameThemeSetting').pipe(
            map(settings => settings[optionType]),
            distinctUntilChanged()
        );
    }

    getTransactions() {
        return this.store.select('gameThemeSetting').pipe(
            map(transactions => transactions),
        );
    }

    getPosition() {
        return this.store.select('gameThemeSetting').pipe(
            map(position => position),
        );
    }

    private convertId(id: string | number): number {
        if (typeof id === 'number') return id;
        return 0;
    }
}
